// The site's colour ramps as plain colours, kept apart from MapColorStrategy.js
// (which builds OpenLayers styles) so the landing page's Big Picture section can
// use them without pulling OpenLayers into the main bundle. MapColorStrategy.js
// re-exports the helpers, so map code can keep importing them from there.

export const HEATMAP_SCHEMES = {
    // Green -> Yellow -> Red gradient
    redgreen: [
        { r: 0,   g: 104, b: 55  }, // deep green (low)
        { r: 26,  g: 152, b: 80  },
        { r: 102, g: 189, b: 99  },
        { r: 166, g: 217, b: 106 },
        { r: 217, g: 239, b: 139 },
        { r: 254, g: 224, b: 139 }, // amber/yellow (midpoint)
        { r: 253, g: 174, b: 97  },
        { r: 244, g: 109, b: 67  },
        { r: 215, g: 48,  b: 39  },
        { r: 165, g: 0,   b: 38  }  // deep red (high)
    ],
    // Pale sky blue -> deep indigo, drawn as discrete bands (see
    // STEPPED_SCHEMES). The bands are spaced evenly in lightness, and the hue
    // drifts from sky towards indigo along the way, so each one is visibly
    // different from its neighbour even when washed out by the fill alpha.
    // The light end stays clearly blue so the lowest band still reads as
    // shaded against the basemap.
    blue: [
        { r: 190, g: 228, b: 245 }, // pale sky blue (low)
        { r: 140, g: 205, b: 238 },
        { r: 84,  g: 172, b: 224 },
        { r: 40,  g: 135, b: 205 },
        { r: 30,  g: 96,  b: 180 },
        { r: 34,  g: 60,  b: 150 },
        { r: 25,  g: 30,  b: 110 }  // deep indigo (high)
    ],
    // Pale mint -> deep teal, the site's own sequential colours (the landing
    // page's price ramp), drawn as discrete bands like 'blue'. Used where a
    // census layer is shaded by the share of one chosen group.
    teal: [
        { r: 222, g: 240, b: 229 }, // pale mint (low)
        { r: 188, g: 224, b: 204 },
        { r: 140, g: 202, b: 176 },
        { r: 79,  g: 168, b: 139 },
        { r: 42,  g: 127, b: 116 },
        { r: 31,  g: 95,  b: 115 },
        { r: 23,  g: 61,  b: 92  }  // deep teal (high)
    ],
    // Brand green -> pale cream -> rust, for deprivation percentiles: the one
    // shaded layer where one end is plainly better off, so the colours say
    // which (green is the least deprived, rust the most). Drawn as bands.
    deprivation: [
        { r: 31,  g: 122, b: 90  }, // brand green (least deprived)
        { r: 111, g: 176, b: 143 },
        { r: 207, g: 229, b: 218 },
        { r: 246, g: 231, b: 196 }, // cream (middle)
        { r: 239, g: 181, b: 122 },
        { r: 201, g: 93,  b: 63  },
        { r: 140, g: 58,  b: 46  }  // deep rust (most deprived)
    ],
    // Cream -> deep rust, the site's crime colours (the crime location bubbles
    // and their popup use the same family). Drawn as bands.
    rust: [
        { r: 251, g: 243, b: 220 }, // cream (lowest)
        { r: 246, g: 220, b: 166 },
        { r: 239, g: 181, b: 122 },
        { r: 225, g: 138, b: 90  },
        { r: 201, g: 93,  b: 63  },
        { r: 158, g: 58,  b: 42  },
        { r: 110, g: 35,  b: 24  }  // deep rust (highest)
    ],
};

// A scheme's bands as solid colours, low to high, for drawing a legend
export const heatmapSchemeColors = (colorScheme) =>
    (HEATMAP_SCHEMES[colorScheme] || []).map((c) => 'rgb('+c.r+','+c.g+','+c.b+')');

// The band a 0-100 value falls in, as a solid colour - the same band the map
// shades it with. For charts that show a score outside the map.
export const heatmapBandColor = (colorScheme, value) => {
    const bands = heatmapSchemeColors(colorScheme);
    if (bands.length===0) return null;
    const t = Math.min(Math.max(value, 0), 100)/100;
    return bands[Math.min(Math.floor(t*bands.length), bands.length-1)];
};


// Non-uniform bucket edges (% change magnitude): most changes fall within
// +/-20%, so that range gets fine 3%-wide buckets for real differentiation;
// 20-30% steps down to 5%-wide buckets; beyond that, 10%-wide buckets.
export const PERCENT_CHANGE_BREAKPOINTS = [3, 6, 9, 12, 15, 18, 20, 25, 30, 40, 50, 60, 70, 80, 90, 100];

const lerpColor = (c1, c2, t) => ({
    r: Math.round(c1.r + (c2.r - c1.r) * t),
    g: Math.round(c1.g + (c2.g - c1.g) * t),
    b: Math.round(c1.b + (c2.b - c1.b) * t),
});

// One shade per breakpoint, interpolated light -> dark so small changes near
// 0% are still a visibly distinct (but light) colour, and large swings
// approach the darkest, most saturated shade. The endpoints are pushed to
// near-white/near-black extremes, and an easing curve (t^0.7) front-loads
// the darkening, so adjacent buckets - especially the small, common ones
// near 0% - are clearly distinguishable rather than blending together.
const buildPercentChangeShades = (lightColor, darkColor) => {
    const n = PERCENT_CHANGE_BREAKPOINTS.length;
    const shades = [];
    for (let i = 0; i < n; i++) {
        const t = Math.pow(i / (n - 1), 0.7);
        shades.push(lerpColor(lightColor, darkColor, t));
    }
    return shades;
};

// Rises are in the brand green and falls in rust, the same pair as the price
// arrows in the area overview and the two ends of the deprivation scale.
export const PERCENT_CHANGE_BANDS = {
    // The light ends are clearly tinted: most changes fall in the first few
    // bands, which otherwise wash out to grey over the basemap.
    positive: buildPercentChangeShades({ r: 168, g: 214, b: 190 }, { r: 16,  g: 78, b: 55 }),
    negative: buildPercentChangeShades({ r: 240, g: 190, b: 160 }, { r: 110, g: 35, b: 24 }),
};

// A fall-to-rise strip of solid colours, for drawing a legend of the bands above
export const percentChangeLegendColors = () => {
    const pick = (bands, idxs) => idxs.map((i) => {
        const c = bands[Math.min(i, bands.length-1)];
        return 'rgb('+c.r+','+c.g+','+c.b+')';
    });
    return [...pick(PERCENT_CHANGE_BANDS.negative, [12, 6, 1]), 'rgb(246,239,220)', ...pick(PERCENT_CHANGE_BANDS.positive, [1, 6, 12])];
};

// A fall-to-rise strip for the Big Picture maps' change views (areaMaps.js): blue for a
// fall, a warm grey for about no change and red for a rise, three shades each way, the
// darkest the largest. Blue and red stay apart for red-green colour blindness.
export const fallRiseLegendColors = () => [
    'rgb(33,102,172)', 'rgb(103,169,207)', 'rgb(209,229,240)',
    'rgb(240,238,233)',
    'rgb(253,219,199)', 'rgb(239,138,98)', 'rgb(178,24,43)',
];

// For the house price and rent change views: orange for a fall and blue for a rise, so a
// rising price doesn't read as bad news, as red would say. Blue and orange stay apart for
// colour blindness.
export const fallOrangeRiseBlueLegendColors = () => [
    'rgb(179,88,6)', 'rgb(241,163,64)', 'rgb(254,224,182)',
    'rgb(240,238,233)',
    'rgb(209,229,240)', 'rgb(103,169,207)', 'rgb(33,102,172)',
];
